// src/pages/AdminDashboard/AdminDashboard.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ROLES } from '../../constants/roles';
import TargetWidget from '../../components/Target/TargetWidget';
import orderService    from '../../services/order.service';
import farmerService   from '../../services/farmer.service';
import dealerService   from '../../services/dealer.service';
import employeeService from '../../services/employee.service';
import farmerVisitService from '../../services/farmerVisit.service';
import './AdminDashboard.css';
import FieldVisitsWidget from '../../components/Dashboard/FieldVisitsWidget';
import BirthdayPopup from '../../components/BirthdayPopup/BirthdayPopup';
import UpcomingBirthdays from '../../components/Dashboard/UpcomingBirthdays';



// ── Only SA and OA see staff count ────────────────────────────────────────────
const STAFF_VISIBLE_ROLES = [ROLES.SUPER_ADMIN, ROLES.OFFICE_ADMIN];

// ── Sparkline ─────────────────────────────────────────────────────────────────
const Sparkline = ({ data, color }) => {
  if (!data?.length) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 80, h = 32;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/>
    </svg>
  );
};

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, color, spark, trend, loading }) => (
  <div className="db-stat-card" style={{ '--accent': color }}>
    <div className="db-stat-top">
      <span className="db-stat-icon">{icon}</span>
      {trend !== undefined && !loading && (
        <span className={`db-stat-trend ${trend >= 0 ? 'up' : 'down'}`}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
        </span>
      )}
    </div>
    {loading ? (
      <div className="db-stat-skeleton"/>
    ) : (
      <>
        <p className="db-stat-value">{value}</p>
        <p className="db-stat-label">{label}</p>
        {sub && <p className="db-stat-sub">{sub}</p>}
        {spark && <div className="db-stat-spark"><Sparkline data={spark} color={color}/></div>}
      </>
    )}
    <div className="db-stat-accent-bar"/>
  </div>
);

// ── Activity Item ─────────────────────────────────────────────────────────────
const ActivityItem = ({ icon, title, sub, time, color }) => (
  <div className="db-activity-item">
    <div className="db-activity-icon" style={{ background: color + '18', color }}>{icon}</div>
    <div className="db-activity-body">
      <p className="db-activity-title">{title}</p>
      <p className="db-activity-sub">{sub}</p>
    </div>
    <span className="db-activity-time">{time}</span>
  </div>
);

// ── Quick Action ──────────────────────────────────────────────────────────────
const QuickAction = ({ icon, label, path, color, navigate, roles, userRole }) => {
  if (roles && !roles.includes(userRole)) return null;
  return (
    <button className="db-quick-action" style={{ '--qa-color': color }} onClick={() => navigate(path)}>
      <span className="db-qa-icon">{icon}</span>
      <span className="db-qa-label">{label}</span>
    </button>
  );
};

// ── Format currency ───────────────────────────────────────────────────────────
const fmtCurrency = (n) => {
  if (!n || isNaN(n)) return '₹0';
  const num = parseFloat(n);
  if (num >= 10000000) return `₹${(num/10000000).toFixed(1)}Cr`;
  if (num >= 100000)   return `₹${(num/100000).toFixed(1)}L`;
  if (num >= 1000)     return `₹${(num/1000).toFixed(1)}K`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const timeAgo = (date) => {
  if (!date) return '';
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  return `${Math.floor(hrs/24)}d ago`;
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { companyId } = useParams();

  const userRole = user?.role;
  const canSeeStaff = STAFF_VISIBLE_ROLES.includes(userRole);
  const currentCompanyId = isSuperAdmin && companyId ? parseInt(companyId) : user?.companyId;

  const [greeting,   setGreeting]   = useState('');
  const [statsLoading, setStatsLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);

  // Stats
  const [totalSales,    setTotalSales]    = useState(0);
  const [deliveredAmt,  setDeliveredAmt]  = useState(0);
  const [orderCount,    setOrderCount]    = useState(0);
  const [farmerCount,   setFarmerCount]   = useState(0);
  const [dealerCount,   setDealerCount]   = useState(0);
  const [staffCount,    setStaffCount]    = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);

  // ── Fetch all stats ─────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const promises = [
        orderService.getOrders().catch(() => ({ success: false, data: [] })),
        farmerService.getFarmerCount().catch(() => ({ success: false, data: { count: 0 } })),
        dealerService.getDealerCount().catch(() => ({ success: false, data: { count: 0 } })),
      ];

      // Only fetch staff count for SA/OA
      if (canSeeStaff) {
        promises.push(
          employeeService.getEmployeeCount().catch(() => ({ success: false, data: { count: 0 } }))
        );
      }

      const results = await Promise.allSettled(promises);

      // Orders
      const ordersRes = results[0].status === 'fulfilled' ? results[0].value : null;
      if (ordersRes?.success && ordersRes.data) {
        const orders = ordersRes.data;
        // Total sales = sum of all confirmed/delivered orders
        const sales = orders
          .filter(o => ['CONFIRMED','PARTIALLY_DISPATCHED','DISPATCHED','DELIVERED'].includes(o.orderStatus))
          .reduce((sum, o) => sum + parseFloat(o.totalAmount || 0), 0);
        // Delivered revenue
        const delivered = orders
          .filter(o => o.orderStatus === 'DELIVERED')
          .reduce((sum, o) => sum + parseFloat(o.totalAmount || 0), 0);
        setTotalSales(sales);
        setDeliveredAmt(delivered);
        setOrderCount(orders.length);
      }

      // Farmers
      const farmersRes = results[1].status === 'fulfilled' ? results[1].value : null;
      if (farmersRes?.success) {
        setFarmerCount(farmersRes.data?.count || 0);
      }

      // Dealers
      const dealersRes = results[2].status === 'fulfilled' ? results[2].value : null;
      if (dealersRes?.success) {
        setDealerCount(dealersRes.data?.count || 0);
      }

      // Staff (SA/OA only)
      if (canSeeStaff && results[3]) {
        const staffRes = results[3].status === 'fulfilled' ? results[3].value : null;
        if (staffRes?.success) {
          setStaffCount(staffRes.data?.count || 0);
        }
      }
    } catch (err) {
      console.error('Dashboard stats error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [canSeeStaff]);

  // ── Fetch recent activity ───────────────────────────────────────────────────
  const fetchActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const [ordersRes, visitsRes] = await Promise.allSettled([
        orderService.getOrders().catch(() => null),
        farmerVisitService.getMyRecentActivity(5).catch(() => null),
      ]);

      const items = [];

      // Recent orders
      if (ordersRes.status === 'fulfilled' && ordersRes.value?.success) {
        const recent = (ordersRes.value.data || [])
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 3);

        for (const o of recent) {
          const statusMeta = {
            PENDING_CONFIRMATION: { icon:'🧾', color:'#ff9800', label:'Order placed' },
            CONFIRMED:            { icon:'✅', color:'#4caf50', label:'Order confirmed' },
            DISPATCHED:           { icon:'🚚', color:'#2196f3', label:'Order dispatched' },
            DELIVERED:            { icon:'📦', color:'#2e7d32', label:'Order delivered' },
            REJECTED:             { icon:'❌', color:'#f44336', label:'Order rejected' },
          };
          const meta = statusMeta[o.orderStatus] || { icon:'📋', color:'#9e9e9e', label:'Order updated' };
          items.push({
            icon:  meta.icon,
            color: meta.color,
            title: meta.label,
            sub:   `${o.orderId} · ${o.dealer?.dealerShopName || 'Dealer'}`,
            time:  timeAgo(o.createdAt),
          });
        }
      }

      // Recent farmer visits
      if (visitsRes.status === 'fulfilled' && visitsRes.value?.success) {
        const visits = (visitsRes.value.data || []).slice(0, 3);
        for (const v of visits) {
          items.push({
            icon:  '🌾',
            color: '#8bc34a',
            title: `${v.visitType === 'FIRST_VISIT' ? 'First visit' : 'Followup visit'} logged`,
            sub:   `${v.farmer?.farmerName || 'Farmer'} · ${v.farmer?.village || ''}`,
            time:  timeAgo(v.visitTime),
          });
        }
      }

      // Sort by time and take top 6
      setRecentActivity(items.slice(0, 6));
    } catch (err) {
      console.error('Activity fetch error:', err);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening');
    if (!currentCompanyId && isSuperAdmin) { navigate('/dashboard/overview'); return; }
    fetchStats();
    fetchActivity();
  }, [currentCompanyId]);

  // Quick actions — role aware
  const quickActions = [
    { icon:'📦', label:'New Order',    path:'/orders/create',   color:'#4caf50', roles: [ROLES.SALES_OFFICER] },
    { icon:'👤', label:'Add Employee', path:'/employee/add',    color:'#2196f3', roles: [ROLES.SUPER_ADMIN, ROLES.OFFICE_ADMIN, ROLES.ADMIN] },
    { icon:'🌾', label:'Add Farmer',   path:'/farmer/add',      color:'#8bc34a' },
    { icon:'🏪', label:'Add Dealer',   path:'/dealer/add',      color:'#ff9800' },
    { icon:'💰', label:'Payments',     path:'/orders/payments', color:'#9c27b0' },
    { icon:'📊', label:'All Orders',   path:'/orders/all',      color:'#00bcd4', roles: [ROLES.EXECUTIVE, ROLES.AREA_MANAGER, ROLES.ZONAL_MANAGER, ROLES.ADMIN, ROLES.OFFICE_ADMIN, ROLES.SUPER_ADMIN] },
  ].filter(a => !a.roles || a.roles.includes(userRole));

  return (
    <div className="db-container">

      {/* ── Welcome ── */}
      <div className="db-welcome">
        <div className="db-welcome-text">
          <p className="db-greeting">{greeting},</p>
          <h2 className="db-name">{user?.firstName} {user?.lastName} 👋</h2>
          <span className="db-role-tag">{user?.role?.displayName || userRole}</span>
        </div>
        <div className="db-welcome-date">
          <p className="db-date-day">{new Date().toLocaleDateString('en-IN', { weekday:'long' })}</p>
          <p className="db-date-full">{new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' })}</p>
        </div>
        {isSuperAdmin && (
          <button className="db-back-btn" onClick={() => navigate('/dashboard/overview')}>← Companies</button>
        )}
      </div>

      {/* ── Upcoming Birthdays — moved to top ── */}
      <div className="db-card" style={{ marginBottom: 20 }}>
        <UpcomingBirthdays />
      </div>
      <BirthdayPopup />

      {/* ── Stats ── */}
      <div className={`db-stats-grid ${canSeeStaff ? 'cols-5' : 'cols-4'}`}>
        <StatCard icon="📈" label="Total Sales"      value={fmtCurrency(totalSales)}   color="#4caf50" loading={statsLoading} spark={[40,55,45,70,65,80,75,90,85,95]} sub="Confirmed orders"/>
        <StatCard icon="🚀" label="Delivered Revenue" value={fmtCurrency(deliveredAmt)} color="#2196f3" loading={statsLoading} spark={[30,40,35,55,50,65,60,70,68,75]} sub="Fully delivered"/>
        <StatCard icon="📦" label="Total Orders"      value={statsLoading ? '—' : orderCount}  color="#ff9800" loading={statsLoading} sub="All statuses"/>
        <StatCard icon="🌾" label="Farmers"           value={statsLoading ? '—' : farmerCount} color="#8bc34a" loading={statsLoading} sub="Registered"/>
        <StatCard icon="🏪" label="Dealers"           value={statsLoading ? '—' : dealerCount} color="#00bcd4" loading={statsLoading} sub="Active"/>
        {/* Total staff — SA/OA only */}
        {canSeeStaff && (
          <StatCard icon="👥" label="Total Staff" value={statsLoading ? '—' : staffCount} color="#9c27b0" loading={statsLoading} sub="Active employees"/>
        )}
      </div>

      {/* ── Main 2-col ── */}
      <div className="db-main-grid">
        <div className="db-card db-target-section">
          <TargetWidget />
        </div>

        <div className="db-right-col">
          {/* Quick Actions */}
          {quickActions.length > 0 && (
            <div className="db-card">
              <div className="db-card-header">
                <h6 className="db-card-title">⚡ Quick Actions</h6>
              </div>
              <div className="db-quick-grid">
                {quickActions.map(a => (
                  <button key={a.label} className="db-quick-action"
                    style={{ '--qa-color': a.color }} onClick={() => navigate(a.path)}>
                    <span className="db-qa-icon">{a.icon}</span>
                    <span className="db-qa-label">{a.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          <div className="db-card db-activity-card">
            <div className="db-card-header">
              <h6 className="db-card-title">🕐 Recent Activity</h6>
              {!activityLoading && recentActivity.length > 0 && (
                <span className="db-activity-badge">{recentActivity.length}</span>
              )}
            </div>
            {activityLoading ? (
              <div className="db-activity-skeleton">
                {[1,2,3,4].map(i => <div key={i} className="db-skeleton-row"/>)}
              </div>
            ) : recentActivity.length === 0 ? (
              <p style={{ color:'#adb5a0', fontSize:13, textAlign:'center', padding:'24px 0' }}>No recent activity</p>
            ) : (
              <div className="db-activity-list">
                {recentActivity.map((a, i) => <ActivityItem key={i} {...a} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom grid ── */}
      <div className="db-bottom-grid">
        <div className="db-card">
          <div className="db-card-header">
            <h6 className="db-card-title">📊 Sales by Month</h6>
            <button className="db-text-btn" onClick={() => navigate('/orders/all')}>View All →</button>
          </div>
          <div className="db-bars">
            {[40,65,45,80,60,90,55,75,50,85,70,95].map((h, i) => (
              <div key={i} className="db-bar-wrap">
                <div className="db-bar" style={{ height:`${h}%` }}/>
                <span className="db-bar-label">
                  {['J','F','M','A','M','J','J','A','S','O','N','D'][i]}
                </span>
              </div>
            ))}
          </div>
        </div>
{['SUPER_ADMIN', 'OFFICE_ADMIN', 'ACCOUNTS'].includes(userRole) && (
  <div className="db-card" style={{ padding: 0 }}>
    <FieldVisitsWidget />
  </div>
)}
        <div className="db-card">
          <div className="db-card-header">
            <h6 className="db-card-title">🏆 Team Breakdown</h6>
          </div>
          <div className="db-team-list">
            {[
              { role:'Zonal Managers',  count:3,  total:5,  color:'#4caf50' },
              { role:'Area Managers',   count:8,  total:10, color:'#2196f3' },
              { role:'Executives',      count:15, total:15, color:'#ff9800' },
              { role:'Sales Officers',  count:12, total:15, color:'#9c27b0' },
              { role:'Field Agents',    count:7,  total:10, color:'#00bcd4' },
            ].map(t => (
              <div key={t.role} className="db-team-row">
                <div className="db-team-dot" style={{ background: t.color }}/>
                <span className="db-team-role">{t.role}</span>
                <div className="db-team-bar-wrap">
                  <div className="db-team-bar" style={{ width:`${(t.count/t.total)*100}%`, background: t.color }}/>
                </div>
                <span className="db-team-count">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;